import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { WebsiteAnalyticsPanelComponent } from '../../components/website-analytics-panel/website-analytics-panel.component';
import {
  BuiltInWebsiteTemplateDefinition,
  BuiltInWebsiteTemplateId,
  FALLBACK_WEBSITE_TEMPLATE
} from '../../models/website-template.models';
import {
  CreateWebsitePageRequest,
  CreateWebsiteRequest,
  PublishWebsiteResult,
  StoreEmailSettings,
  StoreEmailTemplate,
  StoreEmailTemplateKey,
  UpdateWebsiteDataCollectionsRequest,
  UpdateStoreEmailSettingsRequest,
  UpdateStoreEmailTemplateRequest,
  UpdateWebsitePageRequest,
  UpdateWebsiteRequest,
  WebsiteDataCollection,
  WebsiteDeploymentSummary,
  WebsitePageSummary,
  WebsiteStatus,
  WebsiteSummary,
  WebsiteVersionSummary
} from '../../models/website-management.models';
import { WebsiteManagementApiError, WebsiteManagementService } from '../../services/website-management.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { buildPublishedWebsiteHost, buildPublishedWebsiteUrl } from '../../utils/website-runtime-url.utils';

type WebsitesModal = 'createWebsite' | 'editWebsite' | 'createPage' | 'editPage' | null;
type WebsitesWorkspaceTab = 'pages' | 'versions' | 'deployments' | 'data' | 'analytics' | 'emails';

interface WebsitesWorkspaceTabDefinition {
  id: WebsitesWorkspaceTab;
  label: string;
  helpText: string;
}

interface WebsiteFormState {
  name: string;
  slug: string;
  description: string;
  browserTitle: string;
  faviconUrl: string;
  status: WebsiteStatus;
}

interface PageFormState {
  name: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  isHome: boolean;
}

interface EmailSettingsFormState {
  enabled: boolean;
  apiUrl: string;
  smtpServer: string;
  smtpUsername: string;
  smtpPassword: string;
  clearSmtpPassword: boolean;
  fromEmail: string;
  fromName: string;
  fallbackToPlatform: boolean;
  testRecipient: string;
  testTemplateKey: StoreEmailTemplateKey;
}

interface EmailTemplateFormState {
  key: StoreEmailTemplateKey;
  subject: string;
  htmlBody: string;
  textBody: string;
  enabled: boolean;
}

interface DataCollectionFormState {
  publicId: string | null;
  key: string;
  label: string;
  description: string;
  itemsJson: string;
  schemaJson: string;
}

@Component({
  selector: 'app-websites-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ApiLoadErrorComponent, InfoTooltipComponent, WebsiteAnalyticsPanelComponent],
  templateUrl: './websites-page.component.html',
  styleUrls: ['./websites-page.component.scss']
})
export class WebsitesPageComponent implements OnInit {
  private static readonly PAGE_MESSAGE_TIMEOUT_MS = 5000;

  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private pageMessageTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private routeWebsitePublicId: string | null = null;

  isInitialLoading = true;
  hasInitialLoadError = false;
  isPagesLoading = false;
  pagesLoadError = '';
  isVersionsLoading = false;
  versionsLoadError = '';
  isDeploymentsLoading = false;
  deploymentsLoadError = '';
  isDataCollectionsLoading = false;
  dataCollectionsLoadError = '';
  isEmailConfigurationLoading = false;
  emailConfigurationLoadError = '';
  isPublishingWebsite = false;

  websites: WebsiteSummary[] = [];
  pages: WebsitePageSummary[] = [];
  versions: WebsiteVersionSummary[] = [];
  deployments: WebsiteDeploymentSummary[] = [];
  dataCollections: WebsiteDataCollection[] = [];
  emailSettings: StoreEmailSettings | null = null;
  emailTemplates: StoreEmailTemplate[] = [];

  selectedWebsitePublicId: string | null = null;
  selectedPagePublicId: string | null = null;

  activeModal: WebsitesModal = null;
  modalError = '';
  isSubmittingModal = false;
  pageMessage = '';
  editingPagePublicId: string | null = null;
  editingPagePath = '';
  editingPagePathError = '';
  isSavingPagePath = false;
  isSavingWebsiteVersion = false;
  restoringWebsiteVersionPublicId: string | null = null;
  selectedEmailTemplateKey: StoreEmailTemplateKey = 'welcome';
  isSavingEmailSettings = false;
  isSavingEmailTemplate = false;
  isSendingTestEmail = false;
  emailSettingsMessage = '';
  selectedDataCollectionPublicId: string | null = null;
  isSavingDataCollections = false;
  dataCollectionsMessage = '';

  readonly websiteStatuses: WebsiteStatus[] = ['draft', 'published', 'archived'];
  /** Loaded from the API template registry; never hard-coded in this app. */
  websiteTemplates: BuiltInWebsiteTemplateDefinition[] = [FALLBACK_WEBSITE_TEMPLATE];
  private readonly allWorkspaceTabs: WebsitesWorkspaceTabDefinition[] = [
    {
      id: 'pages',
      label: 'Pages',
      helpText: 'Manage website routes, edit paths inline, and open a page in the builder.'
    },
    {
      id: 'versions',
      label: 'Versions',
      helpText: 'Review and restore whole-website saves. Versions include editable pages, website metadata, collections, and translations; deployments remain separate published releases.'
    },
    {
      id: 'deployments',
      label: 'Deployments',
      helpText: 'Each publish creates a frozen deployment snapshot. The active deployment is the live site; older entries remain as history.'
    },
    {
      id: 'data',
      label: 'Data',
      helpText: 'Manage named JSON collections that dynamic builder components render instead of hardcoded page content.'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      helpText: 'Review privacy-safe visitor paths and interactions for this commerce website. Typed values and personal fingerprints are never collected.'
    },
    {
      id: 'emails',
      label: 'Emails',
      helpText: 'Configure SMTP delivery and transactional templates for this commerce website\'s customer flows.'
    }
  ];
  activeWorkspaceTab: WebsitesWorkspaceTab = 'pages';

  websiteForm: WebsiteFormState = this.createEmptyWebsiteForm();
  pageForm: PageFormState = this.createEmptyPageForm();
  emailSettingsForm: EmailSettingsFormState = this.createEmptyEmailSettingsForm();
  emailTemplateForm: EmailTemplateFormState = this.createEmptyEmailTemplateForm();
  dataCollectionForm: DataCollectionFormState = this.createEmptyDataCollectionForm();
  selectedWebsiteTemplateId: BuiltInWebsiteTemplateId = 'blank-site';

  private pagesLoadCycle = 0;
  private versionsLoadCycle = 0;
  private deploymentsLoadCycle = 0;
  private dataCollectionsLoadCycle = 0;
  private dataCollectionsWebsitePublicId: string | null = null;
  private emailConfigurationLoadCycle = 0;
  private emailConfigurationWebsitePublicId: string | null = null;
  private isBackgroundInventoryRefreshInFlight = false;

  constructor(
    private readonly websiteManagementService: WebsiteManagementService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly foregroundRefresh: ForegroundRefreshService
  ) {
    this.destroyRef.onDestroy(() => {
      this.clearPageMessageTimeout();
    });
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.routeWebsitePublicId = params.get('websitePublicId');
        if (this.websites.length) {
          this.selectWebsiteFromRoute();
        }
      });
    this.loadWebsiteTemplates();
    this.loadWebsites();
    this.foregroundRefresh.watch()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshWebsiteInventory());
  }

  get selectedWebsite(): WebsiteSummary | null {
    return this.websites.find((website) => website.publicId === this.selectedWebsitePublicId) ?? null;
  }

  get workspaceTabs(): WebsitesWorkspaceTabDefinition[] {
    return this.isSelectedCommerceWebsite
      ? this.allWorkspaceTabs
      : this.allWorkspaceTabs.filter((tab) => tab.id !== 'emails' && tab.id !== 'analytics');
  }

  private get isSelectedCommerceWebsite(): boolean {
    return this.selectedWebsite?.templateCategory === 'commerce';
  }

  get selectedPage(): WebsitePageSummary | null {
    return this.pages.find((page) => page.publicId === this.selectedPagePublicId) ?? null;
  }

  get selectedDataCollection(): WebsiteDataCollection | null {
    return this.dataCollections.find((collection) => collection.publicId === this.selectedDataCollectionPublicId) ?? null;
  }

  get websiteCountLabel(): string {
    return this.websites.length === 1 ? '1 website' : `${this.websites.length} websites`;
  }

  get selectedWebsiteTemplate(): BuiltInWebsiteTemplateDefinition {
    return this.websiteTemplates.find((template) => template.id === this.selectedWebsiteTemplateId)
      ?? this.websiteTemplates[0]
      ?? FALLBACK_WEBSITE_TEMPLATE;
  }

  private loadWebsiteTemplates(): void {
    this.websiteManagementService.getWebsiteTemplates().subscribe({
      next: (templates) => {
        if (templates.length) {
          this.websiteTemplates = templates;
        }
      },
      error: () => {
        // Keep the blank-site fallback so the modal stays usable offline.
      }
    });
  }

  setActiveWorkspaceTab(tabId: WebsitesWorkspaceTab): void {
    if ((tabId === 'emails' || tabId === 'analytics') && !this.isSelectedCommerceWebsite) {
      this.activeWorkspaceTab = 'pages';
      return;
    }
    this.activeWorkspaceTab = tabId;
    if (tabId === 'data') {
      this.loadDataCollections();
    }
    if (tabId === 'versions') {
      this.loadWebsiteVersions();
    }
    if (tabId === 'emails') {
      this.loadEmailConfiguration();
    }
  }

  private resetUnavailableWorkspaceTab(): void {
    if (!this.isSelectedCommerceWebsite && (this.activeWorkspaceTab === 'emails' || this.activeWorkspaceTab === 'analytics')) {
      this.activeWorkspaceTab = 'pages';
    }
  }

  handleWorkspaceTabKeydown(event: KeyboardEvent, currentTabId: WebsitesWorkspaceTab): void {
    const tabs = this.workspaceTabs;
    const currentIndex = tabs.findIndex((tab) => tab.id === currentTabId);
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    this.setActiveWorkspaceTab(nextTab.id);
    queueMicrotask(() => document.getElementById(`website-workspace-tab-${nextTab.id}`)?.focus());
  }

  loadWebsites(): void {
    const preferredWebsitePublicId = this.routeWebsitePublicId ?? this.selectedWebsitePublicId;
    const preferredPagePublicId = this.selectedPagePublicId;

    this.isInitialLoading = true;
    this.hasInitialLoadError = false;

    this.websiteManagementService.getWebsites()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (websites) => {
          this.runInAngular(() => {
            this.websites = this.sortWebsites(websites);
            this.hasInitialLoadError = false;
            this.isInitialLoading = false;

            const nextWebsitePublicId = preferredWebsitePublicId && this.websites.some((website) => website.publicId === preferredWebsitePublicId)
              ? preferredWebsitePublicId
              : (this.websites[0]?.publicId ?? null);

            this.selectedWebsitePublicId = nextWebsitePublicId;

            this.resetUnavailableWorkspaceTab();

            if (!nextWebsitePublicId) {
              this.pages = [];
              this.versions = [];
              this.deployments = [];
              this.resetDataCollectionsState();
              this.resetEmailConfigurationState();
              this.selectedPagePublicId = null;
              this.pagesLoadError = '';
              this.versionsLoadError = '';
              this.deploymentsLoadError = '';
              this.dataCollectionsLoadError = '';
              this.isPagesLoading = false;
              this.isVersionsLoading = false;
              this.isDeploymentsLoading = false;
              this.isDataCollectionsLoading = false;
              if (this.routeWebsitePublicId) {
                void this.router.navigate(['/websites'], { replaceUrl: true });
              }
              return;
            }

            if (this.routeWebsitePublicId && this.routeWebsitePublicId !== nextWebsitePublicId) {
              this.setPageMessage('The requested website is no longer in your inventory. Showing the first available website.');
            }
            this.syncWebsiteRoute(nextWebsitePublicId, true);
            this.loadPages(nextWebsitePublicId, preferredPagePublicId);
            this.loadDeployments(nextWebsitePublicId);
            if (this.activeWorkspaceTab === 'versions') {
              this.loadWebsiteVersions(true);
            }
            if (this.activeWorkspaceTab === 'data') {
              this.loadDataCollections(true);
            }
            if (this.activeWorkspaceTab === 'emails') {
              this.loadEmailConfiguration(true);
            }
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.hasInitialLoadError = true;
            this.isInitialLoading = false;
          });
        }
      });
  }

  private refreshWebsiteInventory(): void {
    if (this.isInitialLoading || this.isBackgroundInventoryRefreshInFlight || this.isSubmittingModal || this.isPublishingWebsite) {
      return;
    }
    this.isBackgroundInventoryRefreshInFlight = true;
    this.websiteManagementService.getWebsites()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (websites) => this.runInAngular(() => {
          this.isBackgroundInventoryRefreshInFlight = false;
          this.websites = this.sortWebsites(websites);
          const selectedStillExists = this.selectedWebsitePublicId
            && this.websites.some((website) => website.publicId === this.selectedWebsitePublicId);
          if (!selectedStillExists) {
            this.selectWebsiteFromRoute();
          }
        }),
        error: () => this.runInAngular(() => {
          // Preserve the current list and avoid a visible background-refresh
          // error for a transient local proxy/network failure.
          this.isBackgroundInventoryRefreshInFlight = false;
        })
      });
  }

  selectWebsite(websitePublicId: string, syncRoute = true): void {
    if (this.selectedWebsitePublicId === websitePublicId) {
      if (syncRoute) {
        this.syncWebsiteRoute(websitePublicId);
      }
      return;
    }

    this.resetInlinePathEditor();
    this.selectedWebsitePublicId = websitePublicId;
    this.resetUnavailableWorkspaceTab();
    this.selectedPagePublicId = null;
    this.pages = [];
    this.versions = [];
    this.deployments = [];
    this.resetDataCollectionsState();
    this.resetEmailConfigurationState();
    this.pagesLoadError = '';
    this.versionsLoadError = '';
    this.deploymentsLoadError = '';
    this.dataCollectionsLoadError = '';
    this.loadPages(websitePublicId, null);
    this.loadDeployments(websitePublicId);
    if (this.activeWorkspaceTab === 'versions') {
      this.loadWebsiteVersions(true);
    }
    if (this.activeWorkspaceTab === 'data') {
      this.loadDataCollections(true);
    }
    if (this.activeWorkspaceTab === 'emails') {
      this.loadEmailConfiguration(true);
    }
    if (syncRoute) {
      this.syncWebsiteRoute(websitePublicId);
    }
  }

  selectPage(pagePublicId: string): void {
    if (!this.selectedWebsitePublicId) {
      return;
    }

    if (this.selectedPagePublicId === pagePublicId) {
      return;
    }

    this.selectedPagePublicId = pagePublicId;
  }

  isEditingPagePath(page: WebsitePageSummary): boolean {
    return this.editingPagePublicId === page.publicId;
  }

  startPagePathEdit(page: WebsitePageSummary): void {
    if (page.isHome) {
      return;
    }

    this.editingPagePublicId = page.publicId;
    this.editingPagePath = page.path;
    this.editingPagePathError = '';
  }

  cancelPagePathEdit(): void {
    this.resetInlinePathEditor();
  }

  openCreateWebsiteModal(): void {
    this.activeModal = 'createWebsite';
    this.modalError = '';
    this.websiteForm = this.createEmptyWebsiteForm();
    this.selectedWebsiteTemplateId = 'blank-site';
  }

  openEditWebsiteModal(website: WebsiteSummary): void {
    this.activeModal = 'editWebsite';
    this.modalError = '';
    this.websiteForm = {
      name: website.name,
      slug: website.slug,
      description: website.description ?? '',
      browserTitle: website.browserTitle ?? '',
      faviconUrl: website.faviconUrl ?? '',
      status: website.status
    };
  }

  openWebsiteTranslations(website: WebsiteSummary): void {
    this.router.navigate(['/website-translations'], {
      queryParams: {
        website: website.publicId
      }
    });
  }

  openCreatePageModal(): void {
    if (!this.selectedWebsite) {
      return;
    }

    this.activeModal = 'createPage';
    this.modalError = '';
    this.pageForm = {
      ...this.createEmptyPageForm(),
      isHome: this.pages.length === 0
    };
  }

  openEditPageModal(page: WebsitePageSummary): void {
    this.activeModal = 'editPage';
    this.modalError = '';
    this.pageForm = {
      name: page.name,
      slug: page.slug,
      seoTitle: page.seoTitle ?? '',
      seoDescription: page.seoDescription ?? '',
      isHome: page.isHome
    };
  }

  closeModal(): void {
    this.activeModal = null;
    this.modalError = '';
    this.isSubmittingModal = false;
  }

  createWebsite(): void {
    const payload: CreateWebsiteRequest = {
      name: this.websiteForm.name.trim(),
      slug: this.normalizeOptionalText(this.websiteForm.slug),
      description: this.normalizeOptionalText(this.websiteForm.description),
      browserTitle: this.normalizeOptionalText(this.websiteForm.browserTitle),
      faviconUrl: this.normalizeOptionalText(this.websiteForm.faviconUrl),
      templateId: this.selectedWebsiteTemplateId
    };

    if (!payload.name) {
      this.modalError = 'Website name is required.';
      return;
    }

    this.isSubmittingModal = true;
    this.modalError = '';

    this.websiteManagementService.createWebsite(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (website) => {
          this.runInAngular(() => {
            this.upsertWebsite(website);
            this.selectedWebsitePublicId = website.publicId;
            this.selectedPagePublicId = null;
            this.pages = [];
            this.versions = [];
            this.deployments = [];
            this.setPageMessage(`Website "${website.name}" created.`);
            this.closeModal();
            this.loadPages(website.publicId, null);
            this.loadDeployments(website.publicId);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.modalError = error.message;
            this.isSubmittingModal = false;
          });
        }
      });
  }

  selectWebsiteTemplate(templateId: BuiltInWebsiteTemplateId): void {
    this.selectedWebsiteTemplateId = templateId;
  }

  previewWebsiteTemplate(templateId: BuiltInWebsiteTemplateId, event?: Event): void {
    event?.stopPropagation();
    window.open(`${environment.apiUrl}/templates/${templateId}`, '_blank', 'noopener,noreferrer');
  }

  saveWebsite(): void {
    const website = this.selectedWebsite;
    if (!website) {
      return;
    }

    const payload: UpdateWebsiteRequest = {
      name: this.websiteForm.name.trim(),
      slug: this.normalizeOptionalText(this.websiteForm.slug),
      description: this.websiteForm.description.trim() ? this.websiteForm.description.trim() : null,
      browserTitle: this.websiteForm.browserTitle.trim() ? this.websiteForm.browserTitle.trim() : null,
      faviconUrl: this.websiteForm.faviconUrl.trim() ? this.websiteForm.faviconUrl.trim() : null,
      status: this.websiteForm.status
    };

    if (!payload.name) {
      this.modalError = 'Website name is required.';
      return;
    }

    this.isSubmittingModal = true;
    this.modalError = '';

    this.websiteManagementService.updateWebsite(website.publicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedWebsite) => {
          this.runInAngular(() => {
            this.upsertWebsite(updatedWebsite);
            this.setPageMessage(`Website "${updatedWebsite.name}" updated.`);
            this.closeModal();
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.modalError = error.message;
            this.isSubmittingModal = false;
          });
        }
      });
  }

  deleteWebsite(website: WebsiteSummary): void {
    if (!confirm(`Delete website "${website.name}" and all its pages?`)) {
      return;
    }

    this.websiteManagementService.deleteWebsite(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (deletedWebsitePublicId) => {
          this.runInAngular(() => {
            this.websites = this.websites.filter((entry) => entry.publicId !== deletedWebsitePublicId);
            this.setPageMessage(`Website "${website.name}" deleted.`);

            if (this.selectedWebsitePublicId === deletedWebsitePublicId) {
              const nextWebsitePublicId = this.websites[0]?.publicId ?? null;
              this.selectedWebsitePublicId = nextWebsitePublicId;
              this.selectedPagePublicId = null;
              this.pages = [];
              this.versions = [];
              this.deployments = [];

              if (nextWebsitePublicId) {
                this.loadPages(nextWebsitePublicId, null);
                this.loadDeployments(nextWebsitePublicId);
              }
            }
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.setPageMessage(error.message);
          });
        }
      });
  }

  createPage(): void {
    const website = this.selectedWebsite;
    if (!website) {
      return;
    }

    const payload: CreateWebsitePageRequest = {
      name: this.pageForm.name.trim(),
      slug: this.normalizeOptionalText(this.pageForm.slug),
      seoTitle: this.normalizeOptionalText(this.pageForm.seoTitle),
      seoDescription: this.normalizeOptionalText(this.pageForm.seoDescription),
      isHome: this.pageForm.isHome
    };

    if (!payload.name) {
      this.modalError = 'Page name is required.';
      return;
    }

    this.isSubmittingModal = true;
    this.modalError = '';

    this.websiteManagementService.createPage(website.publicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.upsertWebsite(state.website);
            this.setPageMessage(`Page "${state.page.name}" created.`);
            this.closeModal();
            this.loadPages(website.publicId, state.page.publicId);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.modalError = error.message;
            this.isSubmittingModal = false;
          });
        }
      });
  }

  savePage(): void {
    const website = this.selectedWebsite;
    const page = this.selectedPage;

    if (!website || !page) {
      return;
    }

    const payload: UpdateWebsitePageRequest = {
      name: this.pageForm.name.trim(),
      slug: this.normalizeOptionalText(this.pageForm.slug),
      seoTitle: this.pageForm.seoTitle.trim() ? this.pageForm.seoTitle.trim() : null,
      seoDescription: this.pageForm.seoDescription.trim() ? this.pageForm.seoDescription.trim() : null,
      isHome: this.pageForm.isHome
    };

    if (!payload.name) {
      this.modalError = 'Page name is required.';
      return;
    }

    this.isSubmittingModal = true;
    this.modalError = '';

    this.websiteManagementService.updatePage(website.publicId, page.publicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.upsertWebsite(state.website);
            this.setPageMessage(`Page "${state.page.name}" updated.`);
            this.closeModal();
            this.loadPages(website.publicId, state.page.publicId);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.modalError = error.message;
            this.isSubmittingModal = false;
          });
        }
      });
  }

  savePagePath(page: WebsitePageSummary): void {
    const website = this.selectedWebsite;
    if (!website || this.editingPagePublicId !== page.publicId || this.isSavingPagePath) {
      return;
    }

    const nextPath = this.editingPagePath.trim();
    const validationError = this.validateEditablePagePath(page, nextPath);
    if (validationError) {
      this.editingPagePathError = validationError;
      return;
    }

    if (nextPath === page.path) {
      this.resetInlinePathEditor();
      return;
    }

    const payload: UpdateWebsitePageRequest = nextPath === '/'
      ? { isHome: true }
      : { slug: nextPath.slice(1) };

    this.isSavingPagePath = true;
    this.editingPagePathError = '';

    this.websiteManagementService.updatePage(website.publicId, page.publicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.isSavingPagePath = false;
            this.upsertWebsite(state.website);
            this.setPageMessage(state.page.path === '/'
              ? `\"${state.page.name}\" is now the home page.`
              : `Path for \"${state.page.name}\" updated to ${state.page.path}.`);
            this.resetInlinePathEditor();
            this.loadPages(website.publicId, state.page.publicId);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSavingPagePath = false;
            this.editingPagePathError = error.message;
          });
        }
      });
  }

  deletePage(page: WebsitePageSummary): void {
    const website = this.selectedWebsite;
    if (!website) {
      return;
    }

    if (!confirm(`Delete page "${page.name}" from "${website.name}"?`)) {
      return;
    }

    this.websiteManagementService.deletePage(website.publicId, page.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.upsertWebsite(state.website);
            this.pages = state.pages;
            this.setPageMessage(`Page "${page.name}" deleted.`);
            if (this.editingPagePublicId === page.publicId) {
              this.resetInlinePathEditor();
            }

            const nextPagePublicId = this.selectedPagePublicId && state.pages.some((entry) => entry.publicId === this.selectedPagePublicId)
              ? this.selectedPagePublicId
              : (state.pages[0]?.publicId ?? null);

            this.selectedPagePublicId = nextPagePublicId;
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.setPageMessage(error.message);
          });
        }
      });
  }

  loadEmailConfiguration(force = false): void {
    const website = this.selectedWebsite;
    if (!website) {
      return;
    }

    if (!force && this.emailConfigurationWebsitePublicId === website.publicId && this.emailSettings && this.emailTemplates.length > 0) {
      return;
    }

    const loadCycle = ++this.emailConfigurationLoadCycle;
    this.isEmailConfigurationLoading = true;
    this.emailConfigurationLoadError = '';
    this.emailSettingsMessage = '';

    this.websiteManagementService.getEmailConfiguration(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (loadCycle !== this.emailConfigurationLoadCycle) {
              return;
            }
            this.emailSettings = state.settings;
            this.emailTemplates = state.templates;
            this.emailConfigurationWebsitePublicId = website.publicId;
            this.selectedEmailTemplateKey = this.emailTemplates.some((template) => template.key === this.selectedEmailTemplateKey)
              ? this.selectedEmailTemplateKey
              : (this.emailTemplates[0]?.key ?? 'welcome');
            this.emailSettingsForm = this.createEmailSettingsFormFromSettings(state.settings);
            this.syncEmailTemplateForm();
            this.isEmailConfigurationLoading = false;
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            if (loadCycle !== this.emailConfigurationLoadCycle) {
              return;
            }
            this.emailConfigurationLoadError = error.message;
            this.isEmailConfigurationLoading = false;
          });
        }
      });
  }

  saveEmailSettings(): void {
    const website = this.selectedWebsite;
    if (!website || this.isSavingEmailSettings) {
      return;
    }

    const payload: UpdateStoreEmailSettingsRequest = {
      enabled: this.emailSettingsForm.enabled,
      apiUrl: this.emailSettingsForm.apiUrl.trim() || 'https://smtpapi.mxroute.com/',
      smtpServer: this.emailSettingsForm.smtpServer.trim(),
      smtpUsername: this.emailSettingsForm.smtpUsername.trim(),
      fromEmail: this.emailSettingsForm.fromEmail.trim(),
      fromName: this.emailSettingsForm.fromName.trim(),
      fallbackToPlatform: this.emailSettingsForm.fallbackToPlatform,
      clearSmtpPassword: this.emailSettingsForm.clearSmtpPassword
    };

    if (this.emailSettingsForm.smtpPassword.trim()) {
      payload.smtpPassword = this.emailSettingsForm.smtpPassword;
    }

    this.isSavingEmailSettings = true;
    this.emailSettingsMessage = '';

    this.websiteManagementService.updateEmailSettings(website.publicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.runInAngular(() => {
            this.emailSettings = settings;
            this.emailSettingsForm = this.createEmailSettingsFormFromSettings(settings);
            this.isSavingEmailSettings = false;
            this.emailSettingsMessage = 'Email settings saved.';
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSavingEmailSettings = false;
            this.emailSettingsMessage = error.message;
          });
        }
      });
  }

  selectEmailTemplate(templateKey: StoreEmailTemplateKey): void {
    this.selectedEmailTemplateKey = templateKey;
    this.syncEmailTemplateForm();
  }

  saveEmailTemplate(): void {
    const website = this.selectedWebsite;
    if (!website || this.isSavingEmailTemplate) {
      return;
    }

    const payload: UpdateStoreEmailTemplateRequest = {
      subject: this.emailTemplateForm.subject.trim(),
      htmlBody: this.emailTemplateForm.htmlBody.trim(),
      textBody: this.emailTemplateForm.textBody.trim(),
      enabled: this.emailTemplateForm.enabled
    };

    if (!payload.subject || !payload.htmlBody) {
      this.emailSettingsMessage = 'Template subject and HTML body are required.';
      return;
    }

    this.isSavingEmailTemplate = true;
    this.emailSettingsMessage = '';

    this.websiteManagementService.updateEmailTemplate(website.publicId, this.emailTemplateForm.key, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (template) => {
          this.runInAngular(() => {
            this.emailTemplates = this.emailTemplates.map((entry) => entry.key === template.key ? template : entry);
            this.emailTemplateForm = this.createEmailTemplateFormFromTemplate(template);
            this.isSavingEmailTemplate = false;
            this.emailSettingsMessage = 'Email template saved.';
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSavingEmailTemplate = false;
            this.emailSettingsMessage = error.message;
          });
        }
      });
  }

  sendEmailTest(): void {
    const website = this.selectedWebsite;
    const to = this.emailSettingsForm.testRecipient.trim();
    if (!website || !to || this.isSendingTestEmail) {
      this.emailSettingsMessage = 'A test recipient email is required.';
      return;
    }

    this.isSendingTestEmail = true;
    this.emailSettingsMessage = '';

    this.websiteManagementService.sendEmailTest(website.publicId, to, this.emailSettingsForm.testTemplateKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          this.runInAngular(() => {
            this.isSendingTestEmail = false;
            this.emailSettingsMessage = message;
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSendingTestEmail = false;
            this.emailSettingsMessage = error.message;
          });
        }
      });
  }

  loadDataCollections(force = false): void {
    const website = this.selectedWebsite;
    if (!website) {
      return;
    }

    if (!force && this.dataCollectionsWebsitePublicId === website.publicId && this.dataCollections.length > 0) {
      return;
    }

    const loadCycle = ++this.dataCollectionsLoadCycle;
    this.isDataCollectionsLoading = true;
    this.dataCollectionsLoadError = '';
    this.dataCollectionsMessage = '';

    this.websiteManagementService.getDataCollections(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (loadCycle !== this.dataCollectionsLoadCycle) {
              return;
            }

            this.upsertWebsite(state.website);
            this.dataCollections = state.collections;
            this.dataCollectionsWebsitePublicId = website.publicId;
            this.isDataCollectionsLoading = false;

            const selectedCollection = this.selectedDataCollectionPublicId
              ? this.dataCollections.find((collection) => collection.publicId === this.selectedDataCollectionPublicId)
              : null;
            const nextCollection = selectedCollection ?? this.dataCollections[0] ?? null;
            if (nextCollection) {
              this.selectDataCollection(nextCollection.publicId);
            } else {
              this.startNewDataCollection();
            }
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            if (loadCycle !== this.dataCollectionsLoadCycle) {
              return;
            }

            this.dataCollections = [];
            this.dataCollectionsLoadError = error.message;
            this.isDataCollectionsLoading = false;
            this.startNewDataCollection();
          });
        }
      });
  }

  selectDataCollection(collectionPublicId: string): void {
    const collection = this.dataCollections.find((entry) => entry.publicId === collectionPublicId);
    if (!collection) {
      return;
    }

    this.selectedDataCollectionPublicId = collection.publicId;
    this.dataCollectionsMessage = '';
    this.dataCollectionForm = {
      publicId: collection.publicId,
      key: collection.key,
      label: collection.label,
      description: collection.description ?? '',
      itemsJson: this.formatJson(collection.items),
      schemaJson: this.formatJson(collection.schema)
    };
  }

  startNewDataCollection(): void {
    this.selectedDataCollectionPublicId = null;
    this.dataCollectionsMessage = '';
    this.dataCollectionForm = this.createEmptyDataCollectionForm();
  }

  saveDataCollection(): void {
    const website = this.selectedWebsite;
    if (!website || this.isSavingDataCollections) {
      return;
    }

    const key = this.dataCollectionForm.key.trim().toLowerCase();
    const label = this.dataCollectionForm.label.trim();
    const description = this.dataCollectionForm.description.trim();
    const itemsResult = this.parseCollectionJsonArray(this.dataCollectionForm.itemsJson);
    const schemaResult = this.parseCollectionJsonObject(this.dataCollectionForm.schemaJson);

    if (!key) {
      this.dataCollectionsMessage = 'Collection key is required.';
      return;
    }

    if (!/^[a-z0-9][a-z0-9._-]*$/i.test(key)) {
      this.dataCollectionsMessage = 'Collection key can use letters, numbers, dots, underscores, and hyphens only.';
      return;
    }

    if (!label) {
      this.dataCollectionsMessage = 'Collection label is required.';
      return;
    }

    if (!itemsResult.success) {
      this.dataCollectionsMessage = itemsResult.message;
      return;
    }

    if (!schemaResult.success) {
      this.dataCollectionsMessage = schemaResult.message;
      return;
    }

    const duplicate = this.dataCollections.find((collection) =>
      collection.key.toLowerCase() === key && collection.publicId !== this.dataCollectionForm.publicId
    );
    if (duplicate) {
      this.dataCollectionsMessage = `Collection key "${key}" already exists.`;
      return;
    }

    const nextCollections: WebsiteDataCollection[] = [
      ...this.dataCollections.filter((collection) => collection.publicId !== this.dataCollectionForm.publicId),
      {
        publicId: this.dataCollectionForm.publicId ?? crypto.randomUUID(),
        key,
        label,
        description: description || null,
        items: itemsResult.items,
        schema: schemaResult.schema,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ].sort((left, right) => left.key.localeCompare(right.key));

    this.persistDataCollections(website.publicId, nextCollections, `Collection "${label}" saved.`);
  }

  deleteDataCollection(collection: WebsiteDataCollection | null = this.selectedDataCollection): void {
    const website = this.selectedWebsite;
    if (!website || !collection || this.isSavingDataCollections) {
      return;
    }

    if (!confirm(`Delete data collection "${collection.label}"? Dynamic components using ${collection.key} will stop receiving data.`)) {
      return;
    }

    const nextCollections = this.dataCollections.filter((entry) => entry.publicId !== collection.publicId);
    this.persistDataCollections(website.publicId, nextCollections, `Collection "${collection.label}" deleted.`);
  }

  openPageBuilder(page: WebsitePageSummary | null = this.selectedPage): void {
    const website = this.selectedWebsite;
    if (!website || !page) {
      return;
    }

    this.router.navigate(['/website-builder'], {
      queryParams: {
        website: website.publicId,
        page: page.publicId
      }
    });
  }

  trackWorkspaceTab(_index: number, tab: { id: WebsitesWorkspaceTab }): WebsitesWorkspaceTab {
    return tab.id;
  }

  openWebsitePreview(website: WebsiteSummary | null = this.selectedWebsite): void {
    if (!website) {
      return;
    }

    const startPagePublicId = this.pages.find((page) => page.isHome)?.publicId
      || website.homePagePublicId
      || this.pages[0]?.publicId
      || undefined;

    this.router.navigate(['/website-preview'], {
      queryParams: {
        website: website.publicId,
        page: startPagePublicId
      }
    });
  }

  saveWebsiteVersion(): void {
    const website = this.selectedWebsite;
    if (!website || this.isSavingWebsiteVersion) {
      return;
    }

    this.isSavingWebsiteVersion = true;
    this.clearPageMessage();

    this.websiteManagementService.createWebsiteVersion(website.publicId, 'Saved from the Versions workspace')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.isSavingWebsiteVersion = false;
            this.upsertWebsite(state.website);
            this.versions = [state.version, ...this.versions.filter((version) => version.publicId !== state.version.publicId)];
            this.setPageMessage(`Saved website version ${state.version.versionNumber}.`);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSavingWebsiteVersion = false;
            this.setPageMessage(error.message);
          });
        }
      });
  }

  restoreWebsiteVersion(version: WebsiteVersionSummary): void {
    const website = this.selectedWebsite;
    if (!website || this.restoringWebsiteVersionPublicId) {
      return;
    }

    if (!confirm(`Restore the entire editable website to version ${version.versionNumber}? Pages, website metadata, collections, and translations changed after this save will be replaced. Published deployments will not change.`)) {
      return;
    }

    this.restoringWebsiteVersionPublicId = version.publicId;
    this.clearPageMessage();

    this.websiteManagementService.restoreWebsiteVersion(website.publicId, version.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.restoringWebsiteVersionPublicId = null;
            this.upsertWebsite(state.website);
            this.pages = state.pages;
            this.selectedPagePublicId = state.pages.find((page) => page.isHome)?.publicId ?? state.pages[0]?.publicId ?? null;
            this.loadWebsiteVersions(true);
            this.setPageMessage(`Website restored to version ${state.version.versionNumber}. Publish when you are ready to update the live site.`);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.restoringWebsiteVersionPublicId = null;
            this.setPageMessage(error.message);
          });
        }
      });
  }

  openLiveWebsite(website: WebsiteSummary | null = this.selectedWebsite): void {
    if (!website?.subdomain || typeof window === 'undefined') {
      return;
    }

    const activePath = this.selectedPage?.path || '/';
    const liveUrl = buildPublishedWebsiteUrl(
      environment.apiUrl,
      website.subdomain,
      this.authService.getCurrentUserSnapshot()?.nickname,
      activePath
    );
    window.open(liveUrl, '_blank', 'noopener,noreferrer');
  }

  publishWebsite(website: WebsiteSummary | null = this.selectedWebsite): void {
    if (!website || this.isPublishingWebsite) {
      return;
    }

    this.isPublishingWebsite = true;
    this.clearPageMessage();

    this.websiteManagementService.publishWebsite(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: PublishWebsiteResult) => {
          this.runInAngular(() => {
            this.isPublishingWebsite = false;
            this.upsertWebsite(result.website);
            const liveHost = buildPublishedWebsiteHost(
              environment.apiUrl,
              result.deployment.subdomain,
              this.authService.getCurrentUserSnapshot()?.nickname
            );
            this.setPageMessage(`Deployment v${result.deployment.deploymentNumber} is now live on ${liveHost || result.deployment.subdomain}.`);
            this.loadPages(result.website.publicId, this.selectedPagePublicId);
            this.loadDeployments(result.website.publicId);
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isPublishingWebsite = false;
            this.setPageMessage(error.message);
          });
        }
      });
  }

  trackWebsite(_index: number, website: WebsiteSummary): string {
    return website.publicId;
  }

  trackPage(_index: number, page: WebsitePageSummary): string {
    return page.publicId;
  }

  trackWebsiteVersion(_index: number, version: WebsiteVersionSummary): string {
    return version.publicId;
  }

  trackDeployment(_index: number, deployment: WebsiteDeploymentSummary): string {
    return deployment.publicId;
  }

  trackDataCollection(_index: number, collection: WebsiteDataCollection): string {
    return collection.publicId;
  }

  statusBadgeClass(status: WebsiteStatus): string {
    switch (status) {
      case 'published':
        return 'text-bg-success';
      case 'archived':
        return 'text-bg-secondary';
      case 'draft':
      default:
        return 'text-bg-warning';
    }
  }

  private selectWebsiteFromRoute(): void {
    const requestedWebsitePublicId = this.routeWebsitePublicId;
    const requestedWebsite = requestedWebsitePublicId
      ? this.websites.find((website) => website.publicId === requestedWebsitePublicId)
      : null;

    if (requestedWebsite) {
      this.selectWebsite(requestedWebsite.publicId, false);
      return;
    }

    const fallbackWebsite = this.websites[0];
    if (!fallbackWebsite) {
      return;
    }

    if (requestedWebsitePublicId) {
      this.setPageMessage('The requested website is no longer in your inventory. Showing the first available website.');
    }
    this.selectWebsite(fallbackWebsite.publicId, false);
    this.syncWebsiteRoute(fallbackWebsite.publicId, true);
  }

  private syncWebsiteRoute(websitePublicId: string, replaceUrl = false): void {
    if (this.routeWebsitePublicId === websitePublicId) {
      return;
    }

    void this.router.navigate(['/websites', websitePublicId], { replaceUrl });
  }

  private loadPages(websitePublicId: string, preferredPagePublicId: string | null): void {
    const cycle = ++this.pagesLoadCycle;

    this.isPagesLoading = true;
    this.pagesLoadError = '';
    this.resetInlinePathEditor();

    this.websiteManagementService.getPages(websitePublicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (cycle !== this.pagesLoadCycle) {
              return;
            }

            this.upsertWebsite(state.website);
            this.pages = state.pages;
            this.isPagesLoading = false;

            const nextPagePublicId = preferredPagePublicId && state.pages.some((page) => page.publicId === preferredPagePublicId)
              ? preferredPagePublicId
              : (state.pages[0]?.publicId ?? null);

            this.selectedPagePublicId = nextPagePublicId;
            if (this.activeWorkspaceTab === 'versions') {
              this.loadWebsiteVersions(true);
            }
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            if (cycle !== this.pagesLoadCycle) {
              return;
            }

            this.isPagesLoading = false;
            this.pagesLoadError = error.message;
            this.pages = [];
            this.versions = [];
            this.selectedPagePublicId = null;
            this.isVersionsLoading = false;
            this.resetInlinePathEditor();
          });
        }
      });
  }

  loadWebsiteVersions(force = false): void {
    const website = this.selectedWebsite;
    if (!website || (!force && this.versions.length > 0 && !this.versionsLoadError)) {
      return;
    }

    const cycle = ++this.versionsLoadCycle;
    this.isVersionsLoading = true;
    this.versionsLoadError = '';

    this.websiteManagementService.getWebsiteVersions(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (cycle !== this.versionsLoadCycle) {
              return;
            }

            this.upsertWebsite(state.website);
            this.versions = state.versions;
            this.isVersionsLoading = false;
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            if (cycle !== this.versionsLoadCycle) {
              return;
            }

            this.isVersionsLoading = false;
            this.versions = [];
            this.versionsLoadError = error.message;
          });
        }
      });
  }

  private loadDeployments(websitePublicId: string): void {
    const cycle = ++this.deploymentsLoadCycle;

    this.isDeploymentsLoading = true;
    this.deploymentsLoadError = '';

    this.websiteManagementService.getDeployments(websitePublicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (cycle !== this.deploymentsLoadCycle) {
              return;
            }

            this.upsertWebsite(state.website);
            this.deployments = state.deployments;
            this.isDeploymentsLoading = false;
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            if (cycle !== this.deploymentsLoadCycle) {
              return;
            }

            this.isDeploymentsLoading = false;
            this.deployments = [];
            this.deploymentsLoadError = error.message;
          });
        }
      });
  }

  private persistDataCollections(websitePublicId: string, nextCollections: WebsiteDataCollection[], successMessage: string): void {
    const payload: UpdateWebsiteDataCollectionsRequest = {
      collections: nextCollections.map((collection) => ({
        publicId: collection.publicId,
        key: collection.key,
        label: collection.label,
        description: collection.description,
        items: collection.items,
        schema: collection.schema
      }))
    };

    this.isSavingDataCollections = true;
    this.dataCollectionsMessage = '';

    this.websiteManagementService.updateDataCollections(websitePublicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.upsertWebsite(state.website);
            this.dataCollections = state.collections;
            this.dataCollectionsWebsitePublicId = websitePublicId;
            this.isSavingDataCollections = false;
            this.dataCollectionsMessage = successMessage;

            const selectedCollection = this.dataCollectionForm.publicId
              ? this.dataCollections.find((collection) => collection.key === this.dataCollectionForm.key.trim().toLowerCase())
              : this.dataCollections.find((collection) => collection.key === payload.collections[payload.collections.length - 1]?.key);
            if (selectedCollection) {
              this.selectDataCollection(selectedCollection.publicId);
            } else {
              this.startNewDataCollection();
            }
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSavingDataCollections = false;
            this.dataCollectionsMessage = error.message;
          });
        }
      });
  }

  private upsertWebsite(website: WebsiteSummary): void {
    const existingIndex = this.websites.findIndex((entry) => entry.publicId === website.publicId);
    if (existingIndex === -1) {
      this.websites = this.sortWebsites([website, ...this.websites]);
      return;
    }

    const nextWebsites = [...this.websites];
    nextWebsites[existingIndex] = website;
    this.websites = this.sortWebsites(nextWebsites);
  }

  private replacePage(page: WebsitePageSummary): void {
    const pageIndex = this.pages.findIndex((entry) => entry.publicId === page.publicId);
    if (pageIndex === -1) {
      return;
    }

    const nextPages = [...this.pages];
    nextPages[pageIndex] = page;
    this.pages = nextPages;
  }

  private sortWebsites(websites: WebsiteSummary[]): WebsiteSummary[] {
    return [...websites].sort((left, right) => {
      const updatedAtDelta = Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
      if (updatedAtDelta !== 0) {
        return updatedAtDelta;
      }

      return right.publicId.localeCompare(left.publicId);
    });
  }

  private createEmptyWebsiteForm(): WebsiteFormState {
    return {
      name: '',
      slug: '',
      description: '',
      browserTitle: '',
      faviconUrl: '',
      status: 'draft'
    };
  }

  private createEmptyPageForm(): PageFormState {
    return {
      name: '',
      slug: '',
      seoTitle: '',
      seoDescription: '',
      isHome: false
    };
  }

  private createEmptyDataCollectionForm(): DataCollectionFormState {
    return {
      publicId: null,
      key: '',
      label: '',
      description: '',
      itemsJson: '[]',
      schemaJson: '{}'
    };
  }

  private resetDataCollectionsState(): void {
    this.dataCollections = [];
    this.selectedDataCollectionPublicId = null;
    this.dataCollectionForm = this.createEmptyDataCollectionForm();
    this.dataCollectionsLoadError = '';
    this.dataCollectionsMessage = '';
    this.dataCollectionsWebsitePublicId = null;
    this.isDataCollectionsLoading = false;
    this.isSavingDataCollections = false;
  }

  private parseCollectionJsonArray(rawValue: string): {
    success: true;
    items: Record<string, unknown>[];
  } | {
    success: false;
    message: string;
  } {
    const trimmedValue = rawValue.trim() || '[]';
    try {
      const parsedValue = JSON.parse(trimmedValue) as unknown;
      if (!Array.isArray(parsedValue)) {
        return { success: false, message: 'Items JSON must be an array.' };
      }

      if (parsedValue.some((item) => !this.isPlainRecord(item))) {
        return { success: false, message: 'Every item must be a JSON object.' };
      }

      return { success: true, items: parsedValue as Record<string, unknown>[] };
    } catch {
      return { success: false, message: 'Items JSON is not valid.' };
    }
  }

  private parseCollectionJsonObject(rawValue: string): {
    success: true;
    schema: Record<string, unknown>;
  } | {
    success: false;
    message: string;
  } {
    const trimmedValue = rawValue.trim() || '{}';
    try {
      const parsedValue = JSON.parse(trimmedValue) as unknown;
      if (!this.isPlainRecord(parsedValue)) {
        return { success: false, message: 'Schema JSON must be an object.' };
      }

      return { success: true, schema: parsedValue };
    } catch {
      return { success: false, message: 'Schema JSON is not valid.' };
    }
  }

  private formatJson(value: unknown): string {
    return JSON.stringify(value ?? {}, null, 2);
  }

  private isPlainRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private createEmptyEmailSettingsForm(): EmailSettingsFormState {
    return {
      enabled: false,
      apiUrl: 'https://smtpapi.mxroute.com/',
      smtpServer: '',
      smtpUsername: '',
      smtpPassword: '',
      clearSmtpPassword: false,
      fromEmail: '',
      fromName: '',
      fallbackToPlatform: true,
      testRecipient: '',
      testTemplateKey: 'welcome'
    };
  }

  private createEmailSettingsFormFromSettings(settings: StoreEmailSettings): EmailSettingsFormState {
    return {
      enabled: settings.enabled,
      apiUrl: settings.apiUrl || 'https://smtpapi.mxroute.com/',
      smtpServer: settings.smtpServer || '',
      smtpUsername: settings.smtpUsername || '',
      smtpPassword: '',
      clearSmtpPassword: false,
      fromEmail: settings.fromEmail || '',
      fromName: settings.fromName || '',
      fallbackToPlatform: settings.fallbackToPlatform,
      testRecipient: '',
      testTemplateKey: this.selectedEmailTemplateKey
    };
  }

  private createEmptyEmailTemplateForm(): EmailTemplateFormState {
    return {
      key: 'welcome',
      subject: '',
      htmlBody: '',
      textBody: '',
      enabled: true
    };
  }

  private createEmailTemplateFormFromTemplate(template: StoreEmailTemplate): EmailTemplateFormState {
    return {
      key: template.key,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody,
      enabled: template.enabled
    };
  }

  private syncEmailTemplateForm(): void {
    const template = this.emailTemplates.find((entry) => entry.key === this.selectedEmailTemplateKey);
    this.emailTemplateForm = template
      ? this.createEmailTemplateFormFromTemplate(template)
      : this.createEmptyEmailTemplateForm();
  }

  private resetEmailConfigurationState(): void {
    this.emailSettings = null;
    this.emailTemplates = [];
    this.emailSettingsForm = this.createEmptyEmailSettingsForm();
    this.emailTemplateForm = this.createEmptyEmailTemplateForm();
    this.emailConfigurationLoadError = '';
    this.emailSettingsMessage = '';
    this.emailConfigurationWebsitePublicId = null;
    this.isEmailConfigurationLoading = false;
    this.isSavingEmailSettings = false;
    this.isSavingEmailTemplate = false;
    this.isSendingTestEmail = false;
  }

  private normalizeOptionalText(value: string): string | undefined {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  clearPageMessage(): void {
    this.pageMessage = '';
    this.clearPageMessageTimeout();
  }

  private setPageMessage(message: string): void {
    this.pageMessage = message;
    this.clearPageMessageTimeout();

    if (!message) {
      return;
    }

    this.pageMessageTimeoutId = setTimeout(() => {
      this.runInAngular(() => {
        this.pageMessage = '';
        this.pageMessageTimeoutId = null;
      });
    }, WebsitesPageComponent.PAGE_MESSAGE_TIMEOUT_MS);
  }

  private clearPageMessageTimeout(): void {
    if (!this.pageMessageTimeoutId) {
      return;
    }

    clearTimeout(this.pageMessageTimeoutId);
    this.pageMessageTimeoutId = null;
  }

  private validateEditablePagePath(page: WebsitePageSummary, rawPath: string): string {
    if (!rawPath) {
      return 'Path is required.';
    }

    if (!rawPath.startsWith('/')) {
      return 'Path must start with /.';
    }

    if (rawPath.includes('?') || rawPath.includes('#')) {
      return 'Query strings and hash fragments are not supported in page paths.';
    }

    if (rawPath === '/') {
      return '';
    }

    if (page.isHome) {
      return 'The current home page must stay at /. Promote another page to / before moving this one.';
    }

    if (rawPath.length === 1) {
      return 'Path must include a segment after /.';
    }

    if (rawPath.endsWith('/')) {
      return 'Remove the trailing slash from the page path.';
    }

    if (rawPath.includes('//')) {
      return 'Only a single path segment is supported right now.';
    }

    const segment = rawPath.slice(1);
    if (segment.includes('/')) {
      return 'Nested paths are not supported yet. Use a single segment like /about-us.';
    }

    if (segment.length > 160) {
      return 'The path segment must be 160 characters or fewer.';
    }

    if (!/^[a-z0-9-]+$/.test(segment)) {
      return 'Use lowercase letters, numbers, and hyphens only.';
    }

    const duplicate = this.pages.find((entry) => entry.publicId !== page.publicId && entry.path === rawPath);
    if (duplicate) {
      return `This path is already used by "${duplicate.name}".`;
    }

    return '';
  }

  private resetInlinePathEditor(): void {
    this.editingPagePublicId = null;
    this.editingPagePath = '';
    this.editingPagePathError = '';
    this.isSavingPagePath = false;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
